import type { RequestDetailViewState } from "./request-detail-view-types";
import { AlertTriangle, Paperclip, Save, X } from "lucide-react";
import { AutocompleteSelect } from "../../../../../shared/components/AutocompleteSelect";
import { OfferExtraCostsFields } from "../../../../../shared/components/OfferExtraCostsFields";
import { existingOfferReference } from "../../../domain/offer-entry";

type Props = Pick<
  RequestDetailViewState,
  | "detail"
  | "offerComposerOpen"
  | "suppliersLoading"
  | "savingOffer"
  | "editingOfferId"
  | "removingOfferId"
  | "offerFormError"
  | "offerDocument"
  | "setOfferDocument"
  | "extraCostsOpen"
  | "setExtraCostsOpen"
  | "offerForm"
  | "setOfferForm"
  | "supplierAutocompleteOptions"
  | "directOfferTotal"
  | "deliveryWarning"
  | "duplicateSupplierOffer"
  | "closeOfferComposer"
  | "saveDirectOffer"
  | "canManageOffers"
>;
export function RequestOfferComposer({
  detail,
  offerComposerOpen,
  suppliersLoading,
  savingOffer,
  editingOfferId,
  removingOfferId,
  offerFormError,
  offerDocument,
  setOfferDocument,
  extraCostsOpen,
  setExtraCostsOpen,
  offerForm,
  setOfferForm,
  supplierAutocompleteOptions,
  directOfferTotal,
  deliveryWarning,
  duplicateSupplierOffer,
  closeOfferComposer,
  saveDirectOffer,
  canManageOffers,
}: Props) {
  return offerComposerOpen && canManageOffers ? (
                <div
                  className="detail-offer-composer form-modal-surface"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Tedarikçi teklifi ekle"
                >
                  <div className="section-heading compact">
                    <div>
                      <span className="section-kicker">Ticari kayıt</span>
                      <h2>
                        {editingOfferId
                          ? "Tedarikçi teklifini güncelle"
                          : "Tedarikçi teklifi ekle"}
                      </h2>
                      <p className="modal-description">
                        Teklif koşullarını kaydedin; karşılaştırma otomatik
                        güncellensin.
                      </p>
                    </div>
                    <button
                      className="icon-button"
                      type="button"
                      aria-label="Teklif formunu kapat"
                      onClick={closeOfferComposer}
                    >
                      <X size={17} />
                    </button>
                  </div>
                  <div className="offer-request-context">
                    <span>Teklif girilen talep</span>
                    <strong>
                      {detail.quantity.toLocaleString("tr-TR")} {detail.unit}
                    </strong>
                    <small>
                      {detail.id} · {detail.title}
                    </small>
                  </div>
                  {offerFormError ? (
                    <div className="offer-form-error" role="alert">
                      <AlertTriangle size={18} aria-hidden="true" />
                      <div>
                        <strong>Teklif kaydedilemedi</strong>
                        <span>{offerFormError}</span>
                      </div>
                    </div>
                  ) : null}
                  <div className="inline-form-grid detail-offer-form">
                    <AutocompleteSelect
                      label="Tedarikçi"
                      value={offerForm.supplierId}
                      options={supplierAutocompleteOptions}
                      placeholder="Seçiniz"
                      loading={suppliersLoading}
                      disabled={Boolean(editingOfferId)}
                      onChange={(supplierId) =>
                        setOfferForm((current) => ({
                          ...current,
                          supplierId,
                        }))
                      }
                    />
                    <label>
                      Birim fiyat
                      <input
                        inputMode="decimal"
                        value={offerForm.unitPrice}
                        placeholder="Örn. 450"
                        onChange={(event) =>
                          setOfferForm((current) => ({
                            ...current,
                            unitPrice: event.target.value,
                          }))
                        }
                      />
                      <small>
                        {directOfferTotal !== null
                          ? `Toplam: ${directOfferTotal.toLocaleString("tr-TR")} ${offerForm.currency}`
                          : "Miktar ve birim fiyat girildiğinde toplam hesaplanır."}
                      </small>
                    </label>
                    <label>
                      Para birimi
                      <select
                        value={offerForm.currency}
                        onChange={(event) => {
                          const currency = event.target.value;
                          if (currency === "TRY" || currency === "EUR" || currency === "USD") {
                            setOfferForm((current) => ({...current, currency}));
                          }
                        }}
                      >
                        <option>TRY</option>
                        <option>EUR</option>
                        <option>USD</option>
                      </select>
                    </label>
                    <label>
                      Ödeme vadesi (gün)
                      <input
                        type="number"
                        min="0"
                        value={offerForm.paymentDays}
                        onChange={(event) =>
                          setOfferForm((current) => ({
                            ...current,
                            paymentDays: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Teslimat süresi (gün)
                      <input
                        type="number"
                        min="0"
                        value={offerForm.deliveryDays}
                        onChange={(event) =>
                          setOfferForm((current) => ({
                            ...current,
                            deliveryDays: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Geçerlilik tarihi
                      <input
                        type="date"
                        value={offerForm.validUntil}
                        onChange={(event) =>
                          setOfferForm((current) => ({
                            ...current,
                            validUntil: event.target.value,
                          }))
                        }
                      />
                    </label>
                    {deliveryWarning ? (
                      <div
                        className="delivery-date-warning form-field-wide"
                        role="status"
                      >
                        <AlertTriangle size={18} aria-hidden="true" />
                        <div>
                          <strong>İhtiyaç tarihi aşılacak</strong>
                          <span>{deliveryWarning.message}</span>
                        </div>
                      </div>
                    ) : null}
                    {duplicateSupplierOffer ? (
                      <div
                        className="duplicate-offer-warning form-field-wide"
                        role="alert"
                      >
                        <AlertTriangle size={18} aria-hidden="true" />
                        <div>
                          <strong>Bu tedarikçinin teklifi zaten var</strong>
                          <span>
                            {existingOfferReference(duplicateSupplierOffer)}
                            kullanılmalı; aynı talep için ikinci kayıt
                            oluşturulamaz.
                          </span>
                        </div>
                      </div>
                    ) : null}
                    <OfferExtraCostsFields
                      open={extraCostsOpen}
                      values={offerForm}
                      onToggle={() => setExtraCostsOpen((current) => !current)}
                      onChange={(field, value) =>
                        setOfferForm((current) => ({
                          ...current,
                          [field]: value,
                        }))
                      }
                      onDescriptionChange={(value) =>
                        setOfferForm((current) => ({
                          ...current,
                          otherCostDescription: value,
                        }))
                      }
                    />
                    <div className="form-field-wide detail-offer-document">
                      <span>Teklif belgesi</span>
                      <label className="detail-file-picker">
                        <Paperclip size={18} aria-hidden="true" />
                        <span>
                          <strong>
                            {offerDocument?.name ?? "Belge seçin"}
                          </strong>
                          <small>
                            {offerDocument
                              ? "Seçilen belge teklif kaydıyla birlikte yüklenecek."
                              : "PDF, Word, Excel veya görsel · En fazla 20 MB"}
                          </small>
                        </span>
                        <b>{offerDocument ? "Değiştir" : "Dosya seç"}</b>
                        <input
                          type="file"
                          aria-label="Teklif belgesi"
                          accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                          onChange={(event) =>
                            setOfferDocument(event.target.files?.[0] ?? null)
                          }
                        />
                      </label>
                    </div>
                    <label className="form-field-wide">
                      Satın alma notu
                      <textarea
                        rows={2}
                        value={offerForm.procurementNote}
                        onChange={(event) =>
                          setOfferForm((current) => ({
                            ...current,
                            procurementNote: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <div className="inline-form-actions">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={closeOfferComposer}
                    >
                      Vazgeç
                    </button>
                    <button
                      className="approve-button"
                      type="button"
                      disabled={
                        savingOffer ||
                        Boolean(removingOfferId) ||
                        suppliersLoading ||
                        Boolean(duplicateSupplierOffer)
                      }
                      onClick={() => void saveDirectOffer()}
                    >
                      <Save size={15} />
                      {savingOffer
                        ? "Kaydediliyor"
                        : editingOfferId
                          ? "Teklifi güncelle"
                          : "Teklifi kaydet"}
                    </button>
                  </div>
                </div>
              ) : null;
}
